import React, { useState, useEffect } from 'react';
import { Search, Users, ClipboardList, X } from 'lucide-react';
import { useCustomers, useWorkOrders } from '../lib/hooks';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ open, onOpenChange }) => {
  const { customers } = useCustomers();
  const { workOrders } = useWorkOrders();
  const [searchResults, setSearchResults] = useState<{
    customers: any[];
    workOrders: any[];
  }>({
    customers: [],
    workOrders: [],
  });

  // Handle keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  const handleSearch = (term: string) => {
    // Search customers
    const filteredCustomers = customers.filter(customer =>
      `${customer.first_name} ${customer.last_name} ${customer.email || ''} ${customer.phone}`
        .toLowerCase()
        .includes(term.toLowerCase())
    ).slice(0, 5);

    // Search work orders
    const filteredWorkOrders = workOrders.filter(order =>
      `${order.work_order_number} ${order.description} ${order.service_type} ${order.status}`
        .toLowerCase()
        .includes(term.toLowerCase())
    ).slice(0, 5);

    setSearchResults({
      customers: filteredCustomers,
      workOrders: filteredWorkOrders,
    });
  };

  const statusColors = {
    'Received': 'bg-gray-100 text-gray-800',
    'Customer Notified': 'bg-blue-100 text-blue-800',
    'Awaiting Customer Response': 'bg-yellow-100 text-yellow-800',
    'Waiting for Parts': 'bg-orange-100 text-orange-800',
    'Visit Scheduled': 'bg-blue-100 text-blue-800',
    'Visit Completed': 'bg-green-100 text-green-800',
    'Billed': 'bg-green-100 text-green-800',
    'Paid': 'bg-emerald-100 text-emerald-800'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-2xl">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <Command className="rounded-lg border shadow-md">
          <div className="flex items-center border-b px-3">
            <Search className="w-4 h-4 mr-2 shrink-0 opacity-50" />
            <CommandInput
              placeholder="Search customers and work orders..."
              className="flex-1 h-11 bg-transparent outline-none placeholder:text-muted-foreground text-sm"
              onValueChange={handleSearch}
            />
            <div className="flex items-center gap-2">
              <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
                <span className="text-xs">⌘</span>K
              </kbd>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange(false)}
                className="h-6 w-6 p-0 rounded-md"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto px-1 py-2">
            {searchResults.customers.length === 0 && searchResults.workOrders.length === 0 ? (
              <CommandEmpty>No results found.</CommandEmpty>
            ) : (
              <>
                {searchResults.customers.length > 0 && (
                  <CommandGroup heading="Customers">
                    {searchResults.customers.map((customer) => (
                      <CommandItem
                        key={customer.id}
                        onSelect={() => {
                          window.dispatchEvent(new CustomEvent('viewCustomer', { detail: customer }));
                          window.dispatchEvent(new CustomEvent('routeChange', { detail: 'customers' }));
                          onOpenChange(false);
                        }}
                        className="flex items-center gap-3 p-2 cursor-pointer"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50">
                          <Users className="h-5 w-5 text-blue-500" />
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="font-medium truncate">
                            {customer.first_name} {customer.last_name}
                          </span>
                          <span className="text-sm text-muted-foreground truncate">
                            {customer.phone}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {searchResults.workOrders.length > 0 && (
                  <CommandGroup heading="Work Orders">
                    {searchResults.workOrders.map((order) => (
                      <CommandItem
                        key={order.id}
                        onSelect={() => {
                          window.dispatchEvent(new CustomEvent('routeChange', { detail: 'work-orders' }));
                          // Add logic to view specific work order
                          onOpenChange(false);
                        }}
                        className="flex items-center gap-3 p-2 cursor-pointer"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-50">
                          <ClipboardList className="h-5 w-5 text-purple-500" />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">#{order.work_order_number}</span>
                            <Badge variant="secondary" className={statusColors[order.status]}>
                              {order.status}
                            </Badge>
                          </div>
                          <span className="text-sm text-muted-foreground truncate">
                            {order.description}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </>
            )}
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;